import React, { useEffect, useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from '@dnd-kit/core';
import api from '@/lib/api';
import { PageHeader, priorityColor, money } from '@/components/PageHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, FolderKanban, Pencil, Tag, X, ArrowDownUp, Share2 } from 'lucide-react';
import { shareBrandedPDF } from '@/lib/share';
import { toast } from 'sonner';

const COLS = [
  { id: 'pending', label: 'Pendiente', color: '#A6C3B4' },
  { id: 'in_progress', label: 'En Progreso', color: '#3EB489' },
  { id: 'completed', label: 'Completado', color: '#C9A84C' },
  { id: 'blocked', label: 'Bloqueado', color: '#E35D6A' },
];

const PRIO_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };
const emptyTask = { title: '', description: '', priority: 'medium', status: 'pending', project_id: '', assignee: '', due_date: '', recurrence: 'none', tags: [] };

function TaskCard({ task, labels, onDelete, onEdit, onShare }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const c = priorityColor(task.priority);
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 } : {};
  return (
    <div ref={setNodeRef} style={style} data-testid={`kanban-task-${task.id}`}
      className={`pp-glass p-3 group ${isDragging ? 'pp-glow-mint shadow-2xl scale-105' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium flex-1">{task.title}</span>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onShare(task); }} data-testid={`share-task-${task.id}`} title="Compartir tarea" className="opacity-0 group-hover:opacity-70 hover:opacity-100 pp-mint"><Share2 size={13} /></button>
          <button onClick={(e) => { e.stopPropagation(); onEdit(task); }} data-testid={`edit-task-${task.id}`} className="opacity-0 group-hover:opacity-70 hover:opacity-100 pp-mint"><Pencil size={13} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(task.id); }} className="opacity-0 group-hover:opacity-70 hover:opacity-100 text-red-400"><Trash2 size={13} /></button>
          {/* drag handle */}
          <button {...attributes} {...listeners} className="cursor-grab touch-none opacity-30 hover:opacity-80" title="Arrastrar"><ArrowDownUp size={13} /></button>
        </div>
      </div>
      {task.description && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{task.description}</p>}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.text }}>{c.label}</span>
        {(task.tags || []).map((tid) => { const l = labels.find((x) => x.id === tid); if (!l) return null; return (
          <span key={tid} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: l.color + '22', color: l.color, border: `1px solid ${l.color}55` }}>{l.name}</span>
        ); })}
        {task.assignee && <span className="text-[10px] text-muted-foreground">{task.assignee}</span>}
        {task.source === 'ai' && <span className="text-[10px] pp-mint">IA</span>}
      </div>
    </div>
  );
}

function Column({ col, tasks, labels, onDelete, onEdit, onShare }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  return (
    <div ref={setNodeRef} data-testid={`kanban-col-${col.id}`} className={`flex-1 min-w-[240px] rounded-2xl p-3 transition-colors ${isOver ? 'bg-[rgba(62,180,137,0.08)]' : ''}`}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: col.color + '55' }}>
        <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
        <span className="text-sm font-semibold">{col.label}</span>
        <span className="text-xs text-muted-foreground ml-auto">{tasks.length}</span>
      </div>
      <div className="space-y-2 min-h-[120px]">{tasks.map((t) => <TaskCard key={t.id} task={t} labels={labels} onDelete={onDelete} onEdit={onEdit} onShare={onShare} />)}</div>
    </div>
  );
}

export default function TasksPage() {
  const [tab, setTab] = useState('board');
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [labels, setLabels] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [pOpen, setPOpen] = useState(false);
  const [pEditId, setPEditId] = useState(null);
  const [labelMgr, setLabelMgr] = useState(false);
  const [newLabel, setNewLabel] = useState({ name: '', color: '#3EB489' });
  const [form, setForm] = useState(emptyTask);
  const [pForm, setPForm] = useState({ name: '', color: '#3EB489', description: '', budget: 0 });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    const [t, p, l] = await Promise.all([api.get('/tasks'), api.get('/projects'), api.get('/labels').catch(() => ({ data: [] }))]);
    setTasks(t.data); setProjects(p.data); setLabels(l.data);
  };
  useEffect(() => { load(); }, []);

  const sortTasks = (list) => {
    const arr = [...list];
    if (sortBy === 'priority') arr.sort((a, b) => (PRIO_ORDER[a.priority] ?? 9) - (PRIO_ORDER[b.priority] ?? 9));
    else if (sortBy === 'due') arr.sort((a, b) => new Date(a.due_date || '2999') - new Date(b.due_date || '2999'));
    else arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return arr;
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over) return;
    const t = tasks.find((x) => x.id === active.id);
    if (!t || t.status === over.id) return;
    setTasks(tasks.map((x) => x.id === t.id ? { ...x, status: over.id } : x));
    try { await api.patch(`/tasks/${t.id}`, { status: over.id }); } catch (e) { toast.error('Sin permiso'); load(); }
  };

  const openNew = () => { setEditId(null); setForm(emptyTask); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ title: t.title || '', description: t.description || '', priority: t.priority || 'medium', status: t.status || 'pending', project_id: t.project_id || '', assignee: t.assignee || '', due_date: t.due_date ? t.due_date.slice(0, 10) : '', recurrence: t.recurrence || 'none', tags: t.tags || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title) return toast.error('Título requerido');
    const payload = { ...form, project_id: form.project_id || null, due_date: form.due_date || null };
    try {
      if (editId) { await api.patch(`/tasks/${editId}`, payload); toast.success('Tarea actualizada'); }
      else { await api.post('/tasks', payload); toast.success('Tarea creada'); }
      setOpen(false); setEditId(null); setForm(emptyTask); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const openNewProject = () => { setPEditId(null); setPForm({ name: '', color: '#3EB489', description: '', budget: 0 }); setPOpen(true); };
  const openEditProject = (p) => { setPEditId(p.id); setPForm({ name: p.name, color: p.color, description: p.description || '', budget: p.budget || 0 }); setPOpen(true); };
  const saveProject = async () => {
    if (!pForm.name) return toast.error('Nombre requerido');
    const payload = { ...pForm, budget: Number(pForm.budget) };
    try {
      if (pEditId) { await api.patch(`/projects/${pEditId}`, payload); toast.success('Proyecto actualizado'); }
      else { await api.post('/projects', payload); toast.success('Proyecto creado'); }
      setPOpen(false); setPEditId(null); setPForm({ name: '', color: '#3EB489', description: '', budget: 0 }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const delProject = async (id) => { setProjects(projects.filter((x) => x.id !== id)); await api.delete(`/projects/${id}`).catch(() => { toast.error('Sin permiso'); load(); }); };
  const del = async (id) => { setTasks(tasks.filter((x) => x.id !== id)); await api.delete(`/tasks/${id}`).catch(() => { toast.error('Sin permiso'); load(); }); };

  // Compartir tarea con marca: título, prioridad, estado, vencimiento y temas.
  const STATUS_LABEL = { pending: 'Pendiente', in_progress: 'En progreso', completed: 'Completado', blocked: 'Bloqueado' };
  const taskInfo = (t) => {
    const proj = projects.find((p) => p.id === t.project_id);
    const tagNames = (t.tags || []).map((id) => labels.find((l) => l.id === id)?.name).filter(Boolean);
    return { proj, tagNames };
  };
  const shareTask = (t) => {
    const { proj, tagNames } = taskInfo(t);
    const lines = [{ k: 'Prioridad', v: priorityColor(t.priority).label }, { k: 'Estado', v: STATUS_LABEL[t.status] || t.status }];
    if (t.due_date) lines.push({ k: 'Vencimiento', v: new Date(t.due_date).toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' }) });
    if (proj) lines.push({ k: 'Proyecto', v: proj.name });
    if (t.assignee) lines.push({ k: 'Asignado a', v: t.assignee });
    if (tagNames.length) lines.push({ k: 'Etiquetas', v: tagNames.join(', ') });
    lines.push({ k: 'Temas a abordar', v: t.description || 'Sin detalle registrado' });
    shareBrandedPDF(t.title, lines, 'Detalle de la tarea');
  };

  const addLabel = async () => {
    if (!newLabel.name.trim()) return;
    try { const { data } = await api.post('/labels', { ...newLabel, kind: 'tag' }); setLabels([...labels, data]); setNewLabel({ name: '', color: '#3EB489' }); toast.success('Etiqueta creada'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const delLabel = async (id) => { setLabels(labels.filter((l) => l.id !== id)); await api.delete(`/labels/${id}`).catch(() => load()); };
  const toggleTag = (id) => setForm((f) => ({ ...f, tags: f.tags.includes(id) ? f.tags.filter((x) => x !== id) : [...f.tags, id] }));

  const inputCls = 'w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489] text-foreground';

  return (
    <div data-testid="tasks-module">
      <PageHeader kicker="Productividad" title="Tareas & Proyectos"
        actions={<>
          <button data-testid="manage-labels-btn" onClick={() => setLabelMgr(true)} className="h-10 px-4 rounded-xl text-sm font-medium border flex items-center gap-2"><Tag size={16} /> Etiquetas</button>
          <button data-testid="new-project-btn" onClick={openNewProject} className="h-10 px-4 rounded-xl text-sm font-medium border flex items-center gap-2"><FolderKanban size={16} /> Proyecto</button>
          <button data-testid="new-task-btn" onClick={openNew} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}><Plus size={16} /> Tarea</button>
        </>} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab}><TabsList className="pp-glass"><TabsTrigger value="board">Kanban</TabsTrigger><TabsTrigger value="projects">Proyectos</TabsTrigger></TabsList></Tabs>
        {tab === 'board' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Ordenar:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="task-sort" className="h-9 w-[150px] pp-glass"><SelectValue /></SelectTrigger>
              <SelectContent className="pp-glass-strong"><SelectItem value="recent">Reciente</SelectItem><SelectItem value="priority">Prioridad</SelectItem><SelectItem value="due">Fecha límite</SelectItem></SelectContent>
            </Select>
          </div>
        )}
      </div>

      {tab === 'board' ? (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4 pp-scroll" data-testid="kanban-board">
            {COLS.map((col) => <Column key={col.id} col={col} tasks={sortTasks(tasks.filter((t) => t.status === col.id))} labels={labels} onDelete={del} onEdit={openEdit} onShare={shareTask} />)}
          </div>
        </DndContext>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="pp-glass p-5 group relative" data-testid={`project-${p.id}`}>
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEditProject(p)} data-testid={`edit-project-${p.id}`} className="pp-mint hover:opacity-80"><Pencil size={14} /></button>
                <button onClick={() => delProject(p.id)} className="text-red-400 hover:opacity-80"><Trash2 size={14} /></button>
              </div>
              <div className="flex items-center gap-2 mb-3"><span className="w-3 h-3 rounded-full" style={{ background: p.color }} /><span className="font-semibold">{p.name}</span></div>
              <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{p.description}</p>
              <div className="relative w-20 h-20 mx-auto mb-3">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90"><circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" /><circle cx="18" cy="18" r="15" fill="none" stroke={p.color} strokeWidth="3" strokeDasharray={`${p.progress * 0.94} 100`} strokeLinecap="round" /></svg>
                <div className="absolute inset-0 grid place-items-center text-sm font-bold">{p.progress}%</div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>{p.task_count} tareas</span><span>{money(p.budget)}</span></div>
            </div>
          ))}
          {projects.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground col-span-full">Sin proyectos aún.</div>}
        </div>
      )}

      {/* Task dialog (create + edit) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="pp-glass-strong max-h-[90vh] overflow-y-auto pp-scroll"><DialogHeader><DialogTitle>{editId ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título" className={inputCls} />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descripción" className="w-full h-20 p-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489] text-foreground" />
            <div>
              <div className="text-xs text-muted-foreground mb-1">Prioridad</div>
              <div className="flex gap-2 flex-wrap">{['low', 'medium', 'high', 'urgent'].map((p) => { const c = priorityColor(p); return <button key={p} onClick={() => setForm({ ...form, priority: p })} className={`px-3 h-9 rounded-lg text-xs ${form.priority === p ? 'ring-2 ring-offset-0' : 'opacity-50'}`} style={{ background: c.bg, color: c.text }}>{c.label}</button>; })}</div>
            </div>
            {editId && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Estado</div>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="task-status" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="pp-glass-strong">{COLS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between">Etiquetas / incidencias <button type="button" onClick={() => setLabelMgr(true)} className="pp-mint text-[11px] hover:underline">Gestionar</button></div>
              <div className="flex gap-2 flex-wrap">
                {labels.length === 0 && <span className="text-[11px] text-muted-foreground">Crea tus propias etiquetas con "Gestionar".</span>}
                {labels.map((l) => (
                  <button key={l.id} type="button" data-testid={`task-tag-${l.id}`} onClick={() => toggleTag(l.id)} className={`px-2.5 h-8 rounded-lg text-xs border transition-all ${form.tags.includes(l.id) ? '' : 'opacity-40'}`} style={{ background: l.color + '22', color: l.color, borderColor: l.color + '66' }}>{l.name}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="task-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none text-foreground" />
              <input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Asignado a" className="h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none text-foreground" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Proyecto</div>
              <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="task-project" className={inputCls}><SelectValue placeholder="Sin proyecto" /></SelectTrigger>
                <SelectContent className="pp-glass-strong"><SelectItem value="none">Sin proyecto</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 flex-wrap items-center"><span className="text-xs text-muted-foreground">Recurrencia:</span>{[['none', 'Única'], ['daily', 'Diaria'], ['weekly', 'Semanal'], ['monthly', 'Mensual']].map(([v, l]) => <button key={v} type="button" data-testid={`task-recur-${v}`} onClick={() => setForm({ ...form, recurrence: v })} className={`px-2.5 h-8 rounded-lg text-xs border ${form.recurrence === v ? 'pp-mint border-[#3EB489] bg-[rgba(62,180,137,0.12)]' : 'text-muted-foreground'}`}>{l}</button>)}</div>
            <button data-testid="task-save" onClick={save} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>{editId ? 'Guardar cambios' : 'Crear'}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project dialog (create + edit) */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="pp-glass-strong"><DialogHeader><DialogTitle>{pEditId ? 'Editar proyecto' : 'Nuevo proyecto'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="project-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} placeholder="Nombre" className={inputCls} />
            <input value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} placeholder="Descripción" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input type="color" value={pForm.color} onChange={(e) => setPForm({ ...pForm, color: e.target.value })} className="h-11 w-full rounded-xl bg-transparent border" />
              <input type="number" value={pForm.budget} onChange={(e) => setPForm({ ...pForm, budget: e.target.value })} placeholder="Presupuesto" className="h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none text-foreground" />
            </div>
            <button data-testid="project-save" onClick={saveProject} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>{pEditId ? 'Guardar cambios' : 'Crear'}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Label manager */}
      <Dialog open={labelMgr} onOpenChange={setLabelMgr}>
        <DialogContent className="pp-glass-strong" data-testid="label-manager"><DialogHeader><DialogTitle>Etiquetas / incidencias</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Crea tus propias categorías (ej. "Baja", "Medios comunitarios"). Son privadas de tu cuenta.</p>
          <div className="flex gap-2 mt-3">
            <input type="color" value={newLabel.color} onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })} className="h-11 w-12 rounded-xl bg-transparent border shrink-0" />
            <input data-testid="new-label-name" value={newLabel.name} onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && addLabel()} placeholder="Nombre de la etiqueta" className={inputCls} />
            <button data-testid="add-label-btn" onClick={addLabel} className="h-11 px-4 rounded-xl font-semibold text-[#07090A] shrink-0" style={{ background: '#3EB489' }}><Plus size={16} /></button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-h-56 overflow-y-auto pp-scroll">
            {labels.map((l) => (
              <span key={l.id} className="flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-xs border" style={{ background: l.color + '22', color: l.color, borderColor: l.color + '66' }}>
                {l.name}<button onClick={() => delLabel(l.id)} className="hover:opacity-70"><X size={12} /></button>
              </span>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
